import { del, get, post, put } from '@/utils/request'

// Grade List Interfaces
export interface GradeListRequest {
  keyword: string
  page: number
  page_size: number
  sort: boolean
  sort_by: string
}

export interface Grade {
  id: number
  code: string
  name: string
  description: string
  is_deleted: boolean
  created_by: string
  created_at: string
  updated_by: string
  updated_at: string
}

export interface Pagination {
  page: number | null
  page_size: number | null
  total: number
  total_pages: number
}

export interface GradeListResponse {
  success: boolean
  data: {
    items: Grade[]
    pagination: Pagination
  }
  error: any
  message: string
  status_code: number
}

// Grade Subject List Interfaces
export interface GradeSubjectListRequest {
  grade_code: string
  subject_code: string
  keyword: string
  page: number
  page_size: number
  sort: boolean
  sort_by: string
}

export interface GradeSubject {
  id: number
  grade_code: string
  subject_code: string
  grade_name: string
  subject_name: string
  textbook_structure_count: number
  teacher_book_structure_count: number
  is_deleted: boolean
  created_by: string
  created_at: string
  updated_by: string
  updated_at: string
}

export interface GradeSubjectListResponse {
  success: boolean
  data: {
    items: GradeSubject[]
    pagination: Pagination
  }
  error: any
  message: string
  status_code: number
}

// Textbook Structure List Interfaces
export interface TextbookStructureListRequest {
  keyword: string
  grade_code: string
  subject_code: string
  parent_id: number | null
  page: number
  page_size: number | undefined
  sort: boolean
  sort_by: string
}

export interface TextbookStructureListPlatRequest {
  keyword: string
  grade_code: string
  subject_code: string
  parent_id: number | null
  is_published: boolean
  level: number
  page: number
  page_size: number | undefined
  sort: boolean
  sort_by: string
}

export interface TextbookStructure {
  id: number
  grade_code: string
  subject_code: string
  parent_id: number | null
  name: string
  order: number
  description?: string
  level: number
  path?: string
  is_deleted: boolean
  created_by: string
  created_at: string
  updated_by: string
  updated_at: string
  children?: TextbookStructure[]
}

export interface TextbookStructureListResponse {
  success: boolean
  data: {
    items: TextbookStructure[]
    pagination: Pagination
  }
  error: any
  message: string
  status_code: number
}

// Tool Management Interfaces
export interface CreateTextbookStructureRequest {
  name: string
  description: string
  grade_code: string
  subject_code: string
  parent_id: number | null
  knowledge_id: string
  knowledge_document_id: string
  file_dify_id: string
  file_view_id: number
}

export interface UpdateTextbookStructureRequest {
  name: string
  description: string
  learning_space_id: number | null
  file_id: number
  source_type: string
  source_id: number
  id: number
  grade_code: string
  subject_code: string
  parent_id: number | null
  knowledge_id: string
  knowledge_document_id: string
  file_dify_id: string
  file_view_id: number
}

export interface AddAgentDocumentToTextbookStructureRequest {
  structure_id: number
  agent_id: number
  data_source: string | object
  indexing_technique: string
  process_rule: string | object
  doc_form: string | object
  doc_language: string
  retrieval_model: string | object
  embedding_model: string
  embedding_model_provider: string
}

// Tool configs
export interface ToolConfigListRequest {
  keyword: string
  page: number
  page_size: number
  sort: boolean
  sort_by: string
}

export interface LessonPlanTemplateListRequest {
  page: number
  page_size: number
  keyword: string
  tool_template_config_id: number
  sort: boolean
  sort_by: string
  grade_code: string
  subject_code: string
}

export interface CreateToolConfigRequest {
  name: string
  description: string
}

export interface UpdateToolConfigRequest {
  name: string
  description: string
  id: number
}

export interface CreateLessonPlanTemplateRequest {
  name: string
  content: string
  description: string
  grade_code: string | null
  subject_code: string | null
  type: string | null
  tool_template_config_id: number
}

export interface UpdateLessonPlanTemplateRequest {
  name: string
  content: string
  type: string
  description: string
  grade_code: string
  subject_code: string
  tool_template_config_id: number
  id: number
}

/**
 * Get list of grades
 */
export function getGradeList(data: GradeListRequest): Promise<GradeListResponse> {
  return post({
    url: '/classroom-service/grade/list',
    data,
  })
}

/**
 * Get list of grade subjects
 */
export function getGradeSubjectList(data: GradeSubjectListRequest): Promise<GradeSubjectListResponse> {
  return post({
    url: '/classroom-service/grade-subject/list',
    data,
  })
}

// Textbook Structure

/**
 * Get list of textbook structures platform
 */
export function getTextbookStructureListPlatform(data: TextbookStructureListPlatRequest): Promise<TextbookStructureListResponse> {
  return post({
    url: '/classroom-service/textbook-structure/list',
    data,
  })
}

/**
 * Get list of textbook structures
 */
export function getTextbookStructureList(data: TextbookStructureListRequest): Promise<TextbookStructureListResponse> {
  return post({
    url: '/classroom-service/textbook-structure/lst-tree',
    data,
  })
}

/**
 * create textbook structure
 */
export function createTextbookStructure(data: CreateTextbookStructureRequest): Promise<any> {
  return post({
    url: '/classroom-service/textbook-structure/create',
    data,
  })
}

/**
 * update textbook structure
 */
export function updateTextbookStructure(data: UpdateTextbookStructureRequest): Promise<any> {
  return put({
    url: '/classroom-service/textbook-structure/update',
    data,
  })
}

/**
 * delete textbook structure
 */
export function deleteTextbookStructure(structureId: number): Promise<any> {
  return del({
    url: `/classroom-service/textbook-structure/delete?structure_id=${structureId}`,
  })
}

/**
 * add agent document to textbook structure
 */
export function addAgentDocumentToTextbookStructure(data: AddAgentDocumentToTextbookStructureRequest): Promise<any> {
  return post({
    url: `/classroom-service/textbook-structure/create-knowledge-document-chapter?structure_id=${data.structure_id}`,
    data,
  })
}

/**
 * add pdf document to textbook structure
 */
export function addPdfDocumentToTextbookStructure(structureId: number, file: File): Promise<any> {
  const formData = new FormData()
  formData.append('file', file)

  return post({
    url: `/classroom-service/textbook-structure/upload_file_view?structure_id=${structureId}`,
    data: formData,
  })
}

/**
 * publish textbook structure
 */
export function publishTextbookStructure(structureIds: number[]): Promise<any> {
  return put({
    url: '/classroom-service/textbook-structure/publish',
    data: {
      lst_structure_id: structureIds,
    },
  })
}

/**
 * unpublish textbook structure
 */
export function unpublishTextbookStructure(structureIds: number[]): Promise<any> {
  return put({
    url: '/classroom-service/textbook-structure/unpublish',
    data: {
      lst_structure_id: structureIds,
    },
  })
}

// Teacher Structure Interfaces (same as Textbook Structure)
export interface TeacherStructureListRequest {
  keyword: string
  grade_code: string
  subject_code: string
  parent_id: number | null
  page: number
  page_size: number | undefined
  sort: boolean
  sort_by: string
}

export interface TeacherStructure {
  id: number
  grade_code: string
  subject_code: string
  parent_id: number | null
  name: string
  order: number
  description?: string
  level: number
  path?: string
  is_deleted: boolean
  created_by: string
  created_at: string
  updated_by: string
  updated_at: string
  children?: TeacherStructure[]
}

export interface TeacherStructureListResponse {
  success: boolean
  data: {
    items: TeacherStructure[]
    pagination: Pagination
  }
  error: any
  message: string
  status_code: number
}

export interface CreateTeacherStructureRequest {
  name: string
  description: string
  grade_code: string
  subject_code: string
  parent_id: number | null
  knowledge_id: string
  knowledge_document_id: string
  file_dify_id: string
  file_view_id: number
}

export interface UpdateTeacherStructureRequest {
  name: string
  description: string
  learning_space_id: number | null
  file_id: number
  source_type: string
  source_id: number
  id: number
  grade_code: string
  subject_code: string
  parent_id: number | null
  knowledge_id: string
  knowledge_document_id: string
  file_dify_id: string
  file_view_id: number
}

export interface AddAgentDocumentToTeacherStructureRequest {
  structure_id: number
  agent_id: number
  data_source: string | object
  indexing_technique: string
  process_rule: string | object
  doc_form: string | object
  doc_language: string
  retrieval_model: string | object
  embedding_model: string
  embedding_model_provider: string
}

// Teacher Structure

/**
 * Get list of teacher structures
 */
export function getTeacherStructureList(data: TeacherStructureListRequest): Promise<TeacherStructureListResponse> {
  return post({
    url: '/classroom-service/teacher-book-structure/lst-tree',
    data,
  })
}

/**
 * create teacher structure
 */
export function createTeacherStructure(data: CreateTeacherStructureRequest): Promise<any> {
  return post({
    url: '/classroom-service/teacher-book-structure/create',
    data,
  })
}

/**
 * update teacher structure
 */
export function updateTeacherStructure(data: UpdateTeacherStructureRequest): Promise<any> {
  return put({
    url: '/classroom-service/teacher-book-structure/update',
    data,
  })
}

/**
 * delete teacher structure
 */
export function deleteTeacherStructure(structureId: number): Promise<any> {
  return del({
    url: `/classroom-service/teacher-book-structure/delete?structure_id=${structureId}`,
  })
}

/**
 * add agent document to teacher structure
 */
export function addAgentDocumentToTeacherStructure(data: AddAgentDocumentToTeacherStructureRequest): Promise<any> {
  return post({
    url: `/classroom-service/teacher-book-structure/create-knowledge-document-chapter?structure_id=${data.structure_id}`,
    data,
  })
}

/**
 * add pdf document to teacher structure
 */
export function addPdfDocumentToTeacherStructure(structureId: number, file: File): Promise<any> {
  const formData = new FormData()
  formData.append('file', file)

  return post({
    url: `/classroom-service/teacher-book-structure/upload_file_view?structure_id=${structureId}`,
    data: formData,
  })
}

/**
 * publish teacher structure
 */
export function publishTeacherStructure(structureIds: number[]): Promise<any> {
  return put({
    url: '/classroom-service/teacher-book-structure/publish',
    data: {
      lst_structure_id: structureIds,
    },
  })
}

/**
 * unpublish teacher structure
 */
export function unpublishTeacherStructure(structureIds: number[]): Promise<any> {
  return put({
    url: '/classroom-service/teacher-book-structure/unpublish',
    data: {
      lst_structure_id: structureIds,
    },
  })
}
// Tool configs

/**
 * Get list of tool configs
 */
export function getListToolConfigs(data: ToolConfigListRequest): Promise<any> {
  return post({
    url: '/classroom-service/tool-template-config/list',
    data,
  })
}

/**
 * Create tool config
 */
export function createToolConfig(data: CreateToolConfigRequest): Promise<any> {
  return post({
    url: '/classroom-service/tool-template-config/create',
    data,
  })
}

/**
 * Update tool config
 */
export function updateToolConfig(data: UpdateToolConfigRequest): Promise<any> {
  return put({
    url: '/classroom-service/tool-template-config/update',
    data,
  })
}

/**
 * Delete tool config
 */
export function deleteToolConfig(id: number): Promise<any> {
  return del({
    url: `/classroom-service/tool-template-config/delete?config_id=${id}`,
  })
}

/**
 * Get list of lesson plan templates
 */
export function getTLessonPlanConfigList(data: LessonPlanTemplateListRequest): Promise<any> {
  return post({
    url: '/classroom-service/lesson-plan-template/list',
    data,
  })
}

/**
 * Create lesson plan template
 */
export function createLessonPlanTemplate(data: CreateLessonPlanTemplateRequest): Promise<any> {
  return post({
    url: '/classroom-service/lesson-plan-template/create',
    data,
  })
}

/**
 * Update lesson plan template
 */
export function updateLessonPlanTemplate(data: UpdateLessonPlanTemplateRequest): Promise<any> {
  return put({
    url: '/classroom-service/lesson-plan-template/update',
    data,
  })
}

/**
 * Delete lesson plan template
 */
export function deleteLessonPlanTemplate(id: number): Promise<any> {
  return del({
    url: `/classroom-service/lesson-plan-template/delete?template_id=${id}`,
  })
}

/**
 * Generate lesson plan for a specific task with SSE streaming
 * @param data - Request data with task_process_id
 */
export function generateLessonPlan(data: { task_process_id?: number | null; gen_title?: boolean; chat_type: string; text_content?: string; query?: string; document_id?: string | number | null }) {
  return post({
    url: '/classroom-service/agent-lesson-plan/chat-agent-lesson-plan',
    data,
    adapter: 'fetch',
    responseType: 'stream',
    timeout: 300000, // 5 minutes timeout for streaming
  })
}

interface DecreeCircularListRequest {
  grade_subject_id: number
  data_source: string
  indexing_technique: string
  process_rule: string
  doc_form: string
  doc_language: string
  retrieval_model: string
  embedding_model: string
  embedding_model_provider: string
  file_id: number | null
}

export function addMinisterialCircularFile(data: DecreeCircularListRequest): Promise<any> {
  return post({
    url: `/classroom-service/grade-subject/upload-ministerial-circular-file?grade_subject_id=${data.grade_subject_id}`,
    data,
  })
}

export function addMinisterialCircularFileView(gradeSubjectId: number, file: File): Promise<any> {
  const formData = new FormData()
  formData.append('file', file)
  return post({
    url: `/classroom-service/grade-subject/upload-file-view?grade_subject_id=${gradeSubjectId}`,
    data: formData,
  })
}
interface GetFileTaskRequest {
  grade_code: string
  subject_code: string
  textbook_series_id: number
  textbook_lesson_id: number
  start_lesson_id: number
  end_lesson_id: number
}

export function getFileTask(data: GetFileTaskRequest): Promise<any> {
  return post({
    url: '/classroom-service/agent-lesson-plan/get-agent-lesson-plan-knowledge-files',
    data,
  })
}

export function getFileTaskDetail(id: string): Promise<any> {
  return get({
    url: `/classroom-service/agent-lesson-plan/get-files-view-by-task-process-id?task_process_id=${id}`,
  })
}
